/* global google */
import { useEffect } from 'react'
import { getApiKey } from '../../components/Map/getApiKey'

let mapsApiPromise: Promise<void> | null = null
let mapsApiLoaded = false

// Google Maps calls routinely fail with network errors when the user is
// offline; that's expected and not worth logging as an application error.
export function logGoogleMapsError(message: string, err: unknown) {
  if (navigator.onLine) {
    console.error(message, err)
  }
}

function isGoogleMapsLoaded(): boolean {
  // @ts-ignore
  return typeof google !== 'undefined' && Boolean(google.maps)
}

function loadGoogleMaps(apiKey: string): Promise<void> {
  // If Maps loaded successfully before but `google.maps` is gone now (e.g. something
  // cleared `window.google`), force a fresh script load instead of trusting the stale
  // resolved promise — otherwise every caller would be stuck failing forever.
  if (mapsApiLoaded && !isGoogleMapsLoaded()) {
    mapsApiPromise = null
    mapsApiLoaded = false
  }

  if (!mapsApiPromise) {
    mapsApiPromise = new Promise((resolve, reject) => {
      if (isGoogleMapsLoaded()) {
        mapsApiLoaded = true
        resolve()
        return
      }
      const script = document.createElement('script')
      // Do not include `libraries=` with `loading=async` — importLibrary handles lazy loading
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async`
      script.async = true
      script.defer = true
      script.onload = () => {
        mapsApiLoaded = true
        resolve()
      }
      script.onerror = e => {
        logGoogleMapsError('Failed to load Google Maps script:', e)
        reject(e)
      }
      document.head.appendChild(script)
    })
  }
  return mapsApiPromise
}

export function waitForGoogleMaps(): Promise<void> {
  return loadGoogleMaps(getApiKey())
}

export const useLoadScripts = () => {
  useEffect(() => {
    loadGoogleMaps(getApiKey())
  }, [])
}
